import React, { useState } from 'react';

import { Divider } from 'semantic-ui-react';

import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { useDispatch, useSelector } from 'react-redux';

import { DismissibleMessage } from 'components/modules/utils/components';

import { INVALID_FILE_TYPE } from './BusinessInfo.constants';
import { isAnnouncementFormSupported } from './BusinessInfo.utils';
import {
  updateBusinessName,
  updateBusinessLogo,
  updateBusinessAbout,
} from '../../../actions/business';
import { validateOnBlur, inputHasError } from '../../../actions/validation-errors';
import { selectWebsiteContentNodes } from '../../../selectors/website';
import { InlineFieldContainer, InputField } from '../../common';
import FormContainer from '../../common/FormContainer';
import MarkdownField from '../../fields/MarkdownField';
import AnnouncementForm from '../../modules/announcement/components/AnnouncementForm';
import EditFileField from '../../modules/files/components/EditFileField';
import useRecipe from '../../modules/foundation/hooks/use-recipe';
import LayoutComponentList from '../ImagesPage/LayoutComponentList/LayoutComponentList';

import './BusinessInfo.scss';

export default function BusinessInfo() {
  const dispatch = useDispatch();
  const contentNodes = useSelector(selectWebsiteContentNodes);
  const businessName = useSelector((state) => _get(state, 'business.core.value.name'));
  const businessLogo = useSelector((state) => _get(state, 'business.core.value.logo'));
  const about = useSelector((state) => _get(state, 'business.core.value.about[0]'));
  const aboutMarkdown = useSelector((state) => _get(state, 'business.core.value.about_markdown'));
  const layouts = useSelector((state) => _get(state, 'website.layouts.value'));
  const { activePatch } = useRecipe();

  const [invalidLogoFormat, setInvalidLogoFormat] = useState(false);

  const announcementFormSupported = isAnnouncementFormSupported(activePatch);

  const showAnnouncementForm = !_isEmpty(layouts) && announcementFormSupported;
  const showLegacyMessageDisplay = !_isEmpty(layouts) && !announcementFormSupported;

  function handleNameChange(e, target) {
    const { value } = target;

    dispatch(updateBusinessName({ value }));
  }

  function handleAboutChange(value) {
    dispatch(updateBusinessAbout({ value, index: 0 }));
  }

  function handleImageChange(e) {
    const file = e.target.files[0];
    const url = window.URL.createObjectURL(file);

    const image = { url, source: 'LOCAL', file };

    dispatch(updateBusinessLogo({ field: 'logo', fieldValue: image }));
  }

  function handleImageDelete() {
    dispatch(
      updateBusinessLogo({
        field: 'logo',
        fieldValue: { url: '', source: 'LOCAL', file: '' },
      }),
    );
  }

  return (
    <div className="business-info">
      <FormContainer loadedKeyPath={['business', 'core']}>
        <InlineFieldContainer title={<h2>Business Name</h2>} enableDelete={false}>
          <InputField
            style={{ width: '50%' }}
            value={businessName}
            placeholder="My Business"
            onChange={handleNameChange}
            error={inputHasError('/core/profile.Business Name')}
            onValidate={() =>
              dispatch(validateOnBlur({ routeAndInput: '/core/profile.Business Name' }))
            }
          />
        </InlineFieldContainer>
        <InlineFieldContainer title={<h2>Logo</h2>} enableDelete={false} alignItems="flex-start">
          <EditFileField
            url={businessLogo}
            onChange={handleImageChange}
            onDelete={handleImageDelete}
          >
            <EditFileField.FileInput onFileTypeError={() => setInvalidLogoFormat(true)} />
            <EditFileField.FileEditorModal />
          </EditFileField>
          {invalidLogoFormat && (
            <div className="notification-error-message">
              <DismissibleMessage
                size="small"
                initialVisible
                delay={8}
                error
                header="File Upload Error"
                content={INVALID_FILE_TYPE}
                onDismiss={() => setInvalidLogoFormat(false)}
              />
            </div>
          )}
        </InlineFieldContainer>
        {_isEmpty(contentNodes) && (
          <InlineFieldContainer title={<h2>About</h2>} enableDelete={false}>
            <MarkdownField value={aboutMarkdown || about} onChange={handleAboutChange} />
          </InlineFieldContainer>
        )}
        {showLegacyMessageDisplay && <LayoutComponentList layouts={layouts} />}
        {showAnnouncementForm && (
          <>
            <Divider />
            <AnnouncementForm />
          </>
        )}
      </FormContainer>
    </div>
  );
}
